import React, {useCallback, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import {selectCurrentUser} from 'app/hooks/reduxCreateSelectorHooks';
import LivetickerAssigned from 'app/components/match/matchLive/LivetickerAssigned';
import LivetickerAvailable from 'app/components/match/matchLive/LivetickerAvailable';
import LivetickerAvailableInX from 'app/components/match/matchLive/LivetickerAvailableInX';
import LivetickerLoadingIndicator from 'app/components/match/matchLive/LivetickerLoadingIndicator';
import {captureSentryException} from 'app/services/sentryLogging';
import ReportLiveResult from 'app/components/match/liveResult/ReportLiveResult';
import {useIsSofttickerEligible} from 'app/hooks/useAllowSoftTicker';
import is from '@sindresorhus/is';

const NO_TICKER_FLAGS = ['cancelled', 'aborted', 'verdict'];

const LivetickerAssignment = ({
  matchInfo,
  reloadData,
  assignTickerAt,
  tickerFailure,
  isLoading,
  setPermissions,
  openAuthDialog,
}) => {
  const {fuPaUser, initializing} = useSelector(selectCurrentUser);
  const isAvailable = useCallback(() => {
    const isReserveable = matchInfo?.tickerReservableUntil
      ? new Date().toISOString() <= new Date(matchInfo?.tickerReservableUntil).toISOString()
      : false;
    const tickerIsBlocked = NO_TICKER_FLAGS.some(flag => matchInfo.flags?.includes(flag));
    const matchHasResult = is.number((matchInfo.homeGoal ?? matchInfo.awayGoal));
    return isReserveable && !tickerIsBlocked && !matchHasResult;
  }, [matchInfo.tickerReservableUntil, matchInfo.homeGoal, matchInfo.awayGoal, matchInfo.flags]);
  const hasTickerFlag = useMemo(() => matchInfo.flags?.includes('ticker'), [matchInfo.flags]);
  const exceptionSent = useRef(null);
  const allowSoftticker = useIsSofttickerEligible(matchInfo);

  if (!fuPaUser.id && hasTickerFlag) {
    return null;
  }

  if (isLoading || initializing) {
    if (hasTickerFlag) {
      return null;
    }
    return <LivetickerLoadingIndicator message='Tickerstatus wird geladen...' />;
  }

  switch (tickerFailure?.code) {
    case 'ticker_exists_for_user':
      if (!hasTickerFlag && matchInfo?.tickerAuthor && !exceptionSent.current) {
        captureSentryException(new Error('Ticker Enrollment Mismatch: Flag absent, but Author set.'), {
          extra: {
            matchSlug: matchInfo.slug,
            time: new Date().toString(),
            tickerFlag: hasTickerFlag,
            author: !!matchInfo.tickerAuthor,
          },
        });
        exceptionSent.current = true;
      }
      return (
        <LivetickerAssigned
          match={{slug: matchInfo.slug, id: matchInfo.id, section: matchInfo.section}}
          reloadData={reloadData}
          setPermissions={setPermissions}
        />
      );
    case 'ticker_exists':
    case 'no_permission':
      return null;
    case 'soft_ticker_exists':
    case 'post_register_phase':
      if (allowSoftticker) {
        return <ReportLiveResult match={matchInfo} openAuthDialog={openAuthDialog} />;
      }
      return null;
    case 'pre_register_phase':
      return <LivetickerAvailableInX assignAtTimestamp={assignTickerAt} />;
    default:
      if (allowSoftticker) {
        return <ReportLiveResult match={matchInfo} openAuthDialog={openAuthDialog} />;
      }
      if (isAvailable()) {
        return (
          <LivetickerAvailable
            fuPaUser={fuPaUser}
            reloadData={reloadData}
            matchSlug={matchInfo.slug}
            openAuthDialog={openAuthDialog}
          />
        );
      }
      return null;
  }
};

export default LivetickerAssignment;
